//@ts-check
import React, {Component} from 'react';
import {func, shape} from 'prop-types';
import classNames from 'classnames';
import {default as TextInput} from '@sinclair-digital/sbg-storyline-components/src/components/forms/TextInput';
import {default as Select} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Select';
import {default as Option} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Option';
import {default as Field} from '@sinclair-digital/sbg-storyline-components/src/components/Field';
import {default as Button} from '@sinclair-digital/sbg-storyline-components/src/components/Button';
import {default as IconButton} from '@sinclair-digital/sbg-storyline-components/src/components/IconButton';
import {integrationSettingsShape, providerNames} from './Integration';
import styles from './IntegrationSettings.scss';
import TagCloud from 'components/TagCloud';
import Tag from 'components/Tag';
import {trashO} from 'react-icons-kit/fa/trashO';
import data from './data.json';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';

const aiStoryAudioEnabled = () => getFeatureFlag('aiReadStoryAloud');

class PropertyIntegration extends Component {
    state = {
        providerSelect: providerNames[0],
    };

    static propTypes = {
        addEPGProvider: func.isRequired,
        addCustomIntegrationPair: func.isRequired,
        removeEPGProvider: func.isRequired,
        integrationSettings: shape(integrationSettingsShape),
        onChange: func.isRequired,
        onDeleteCustomIntegrationPair: func.isRequired,
        onCustomIntegrationPairChange: func.isRequired,
        onValidatedCustomIntegrationPairChange: func.isRequired,
        onValidatedInputChange: func.isRequired,
    };

    addTag = val => {
        const {providerSelect} = this.state;
        const idTypeMap = {
            TitanTV: {registrationKey: val},
            Gracenote: {stationId: val},
        };
        const tagData = {
            providerName: providerSelect,
            providerConfig: idTypeMap[providerSelect],
        };
        this.props.addEPGProvider(tagData);
    };

    renderTag = tagData => {
        const {providerName} = tagData;
        const tagPropMap = {
            gracenote: {
                className: styles.Gracenote,
                header: tagData.providerConfig.stationId,
                tagBody: providerName,
            },
            titantv: {
                className: styles.TitanTV,
                header: tagData.providerConfig.registrationKey,
                tagBody: providerName,
            },
        };

        const provider = providerName.toLowerCase();
        const tagProps = tagPropMap[provider];

        return (
            tagProps && (
                <Tag onRemoveTag={() => this.props.removeEPGProvider(provider, tagProps.header)} {...tagProps} />
            )
        );
    };

    renderInputPair = (val, idx) => {
        const {
            integrationSettings,
            onDeleteCustomIntegrationPair,
            onCustomIntegrationPairChange,
            onValidatedCustomIntegrationPairChange,
        } = this.props;

        const {customIntegrationPairs} = integrationSettings;

        return [
            <TextInput
                className={classNames('sda-input-text-full', styles.keyValPair)}
                name={`${idx}-key`}
                key={`${idx}-key`}
                maxLength="256"
                errorMessage={customIntegrationPairs[idx].key.errorMessage}
                onChange={onValidatedCustomIntegrationPairChange(idx)}
                placeholder="Custom key"
                value={customIntegrationPairs[idx].key.value}
            />,

            <TextInput
                className={classNames([styles.keyValPair, 'sda-input-text-full'])}
                name={`${idx}-value`}
                key={`${idx}-value`}
                maxLength="256"
                onChange={onCustomIntegrationPairChange(idx)}
                placeholder="Custom value"
                value={customIntegrationPairs[idx].val}
            />,

            <IconButton
                icon={trashO}
                key={`trash-${idx}`}
                className={styles.trashIcon}
                onClick={onDeleteCustomIntegrationPair(idx)}
                theme="transparent"
            />,
        ];
    };

    onSelectChange = ({target: {value}}) => this.setState({providerSelect: value});

    render() {
        const {addCustomIntegrationPair, integrationSettings, onChange, onValidatedInputChange} = this.props;
        const {
            dfpAdUnitId,
            dfpNetworkId,
            googleAnalyticsId,
            googleTagManagerId,
            customIntegrationPairs,
            androidMorningApp,
            androidStationApp,
            androidWatchLiveApp,
            androidWeatherApp,
            epgId,
            epgProviders,
            iOSMorningApp,
            iOSStationApp,
            iOSWatchLiveApp,
            iOSWeatherApp,
            readAloudVoiceId,
        } = integrationSettings;
        return (
            <div className={styles.IntegrationSettings}>
                <div className={styles.columnLeft}>
                    <h3 className={styles.psH3}>Basic Integration</h3>
                    {/* DFP NETWORK ID */}
                    <Field label="DFP Network ID">
                        <TextInput
                            className="sda-input-text-full"
                            name="dfpNetworkId"
                            maxLength="256"
                            errorMessage={dfpNetworkId.errorMessage}
                            onChange={onValidatedInputChange}
                            placeholder="4756"
                            value={dfpNetworkId.value}
                        />
                    </Field>

                    {/* DFP SITE AD UNIT ID */}
                    <Field label="DFP Site Ad Unit ID">
                        <TextInput
                            className="sda-input-text-full"
                            name="dfpAdUnitId"
                            maxLength="256"
                            errorMessage={dfpAdUnitId.errorMessage}
                            onChange={onValidatedInputChange}
                            placeholder="WBFF News"
                            value={dfpAdUnitId.value}
                        />
                    </Field>

                    {/* GOOGLE ANALYTICS ID */}
                    <Field label="Google Analytics ID">
                        <TextInput
                            className="sda-input-text-full"
                            errorMessage={googleAnalyticsId.errorMessage}
                            name="googleAnalyticsId"
                            maxLength="256"
                            onChange={onValidatedInputChange}
                            placeholder="UA-XXXXX-X"
                            value={googleAnalyticsId.value}
                        />
                    </Field>

                    {/* GOOGLE TAG MANAGER ID */}
                    <Field label="Google Tag Manager Id">
                        <TextInput
                            className="sda-input-text-full"
                            name="googleTagManagerId"
                            maxLength="256"
                            onChange={onChange}
                            placeholder="GTM-XXXX"
                            value={googleTagManagerId}
                        />
                    </Field>

                    {/* Program Guide ID */}
                    <Field label="EPG ID">
                        <TextInput
                            className="sda-input-text-full"
                            name="epgId"
                            maxLength="256"
                            onChange={onChange}
                            placeholder="xxxx"
                            value={epgId}
                        />
                    </Field>

                    <div className={styles.inputSection}>
                        <h3 className={classNames(styles.psH3, styles.integrationHeading)}>New Integrations</h3>
                        <Button displayType="default" onClick={addCustomIntegrationPair}>
                            + add integration
                        </Button>

                        {customIntegrationPairs.length > 0 ? (
                            <div className={styles.keyValuePairs}>
                                <div className={styles.kvHeaderKey}>Key</div>
                                <div className={styles.kvHeaderVal}>Value</div>

                                {customIntegrationPairs.map(this.renderInputPair)}
                            </div>
                        ) : (
                            <div className={classNames(styles.muted, styles.keyValuePairs)}>
                                No new integrations added
                            </div>
                        )}
                    </div>

                    <div className={styles.inputSection}>
                        <h3 className={classNames(styles.psH3, styles.integrationHeading)}>EPG Providers</h3>
                        <Field label="Registration Key or Station ID">
                            <TagCloud
                                placeholder="xxxxxxx"
                                className={styles.TagCloudField}
                                inputClassName={styles.tagCloudInput}
                                addTag={this.addTag}
                                tags={epgProviders.map(this.renderTag)}>
                                <div className={classNames(styles.providerSelect, 'fa-angle-down')}>
                                    <label className={styles.selectLabel}>Provider Name</label>
                                    <Select
                                        name="epgProviders"
                                        onChange={this.onSelectChange}
                                        value={this.state.providerSelect}>
                                        {providerNames.map(provider => (
                                            <Option label={provider} key={provider} value={provider} />
                                        ))}
                                    </Select>
                                </div>
                            </TagCloud>
                        </Field>
                    </div>
                </div>
                <div className={styles.columnRight}>
                    <h3 className={styles.psH3}>iOS App Store Identification</h3>
                    {/* IOS STATION APP */}
                    <Field label="Station App">
                        <TextInput
                            className="sda-input-text-full"
                            name="iOSStationApp"
                            maxLength="1024"
                            onChange={onChange}
                            value={iOSStationApp}
                        />
                    </Field>

                    {/* IOS MORNING APP */}
                    <Field label="Morning App">
                        <TextInput
                            className="sda-input-text-full"
                            name="iOSMorningApp"
                            maxLength="1024"
                            onChange={onChange}
                            value={iOSMorningApp}
                        />
                    </Field>

                    {/* IOS WEATHER APP */}
                    <Field label="Weather App">
                        <TextInput
                            className="sda-input-text-full"
                            name="iOSWeatherApp"
                            maxLength="1024"
                            onChange={onChange}
                            value={iOSWeatherApp}
                        />
                    </Field>

                    {/* IOS WATCH LIVE APP */}
                    <Field label="Watch Live App">
                        <TextInput
                            className="sda-input-text-full"
                            name="iOSWatchLiveApp"
                            maxLength="1024"
                            onChange={onChange}
                            value={iOSWatchLiveApp}
                        />
                    </Field>

                    <div className={styles.inputSection}>
                        <h3 className={classNames(styles.psH3, styles.integrationHeading)}>
                            Android Marketplace Identification
                        </h3>

                        {/* ANDROID STATION APP */}
                        <Field label="Station App">
                            <TextInput
                                className="sda-input-text-full"
                                name="androidStationApp"
                                maxLength="1024"
                                onChange={onChange}
                                value={androidStationApp}
                            />
                        </Field>

                        {/* ANDROID MORNING APP */}
                        <Field label="Morning App">
                            <TextInput
                                className="sda-input-text-full"
                                name="androidMorningApp"
                                maxLength="1024"
                                onChange={onChange}
                                value={androidMorningApp}
                            />
                        </Field>

                        {/* ANDROID WEATHER APP */}
                        <Field label="Weather App">
                            <TextInput
                                className="sda-input-text-full"
                                name="androidWeatherApp"
                                maxLength="1024"
                                onChange={onChange}
                                value={androidWeatherApp}
                            />
                        </Field>

                        {/* ANDROID WATCH LIVE APP */}
                        <Field label="Watch Live App">
                            <TextInput
                                className="sda-input-text-full"
                                name="androidWatchLiveApp"
                                maxLength="1024"
                                onChange={onChange}
                                value={androidWatchLiveApp}
                            />
                        </Field>
                    </div>
                    {/* AI Read Story Aloud */}
                    {aiStoryAudioEnabled() ?
                        <div className={styles.inputSection}>
                            <h3 className={classNames(styles.psH3, styles.integrationHeading)}>
                                Read Aloud Audio
                            </h3>
                            <Field label="Voice">
                                <div className="sda-select-firefox fa-angle-down">
                                    <Select
                                        name="readAloudVoiceId"
                                        onChange={onChange}
                                        value={readAloudVoiceId}
                                    >
                                        {data.voiceData.map(({name, voiceID}) => (
                                            <Option
                                                key={voiceID}
                                                label={name}
                                                value={voiceID}
                                            />
                                        ))}
                                    </Select>
                                </div>
                            </Field>
                        </div>
                        : null
                    }
                </div>
            </div>
        );
    }
}

export default PropertyIntegration;
